import { parsePhoneNumber, type Country } from "react-phone-number-input";

/**
 * 전화번호 표기 변환.
 *
 * <p>저장·전송 형식은 전 국가 공통으로 libphonenumber 의 national number 를 쓴다.
 * trunk prefix(선행 0) 를 떼야 하는지는 국가마다 다르고, 자체 규칙으로 판단할 수 없다 —
 * 한국·일본·영국은 표기용이라 떼지만, 이탈리아(유선)·코트디부아르·베냉·콩고·가봉은
 * 0 이 번호의 일부라 떼면 다른 번호가 된다. 그래서 판단은 전부 라이브러리에 맡긴다.
 *
 *   01012345678 / 1012345678  (KR) → 1012345678
 *   09012345678 / 9012345678  (JP) → 9012345678
 *   0612345678                (IT) → 0612345678   0 유지
 *
 * <p>선행 0 이 필요한 곳은 다날 본인인증 요청 하나뿐이며, 그 변환은 요청 페이로드를
 * 만드는 시점에 {@link toDanalCtn} 으로만 적용한다.
 */

/**
 * 저장·전송용 번호로 변환한다. 파싱에 실패하면 숫자만 남겨 그대로 돌려준다
 * (유효성 판단은 호출부의 isValidPhoneNumber 가 담당).
 */
export const toNationalNumber = (raw: string, country: Country): string => {
    const digits = (raw ?? "").replace(/[^0-9]/g, "");
    if (!digits) return "";
    try {
        const parsed = parsePhoneNumber(digits, country);
        return parsed ? parsed.nationalNumber : digits;
    } catch {
        return digits;
    }
};

/**
 * 다날 본인인증 요청용 국내 표기로 변환한다.
 *
 * 다날은 통신사 가입 정보와 대조하므로 국내 표기("01012345678")를 요구하는데,
 * 우리 저장 형식에는 선행 0 이 없다. 이 함수는 다날 요청을 만들 때만 쓰고
 * signUpData.ctn 같은 도메인 값은 건드리지 않는다.
 *
 *   1012345678  → 01012345678
 *   01012345678 → 01012345678   이미 붙어 있으면 그대로
 */
export const toDanalCtn = (ctn: string): string => {
    const digits = (ctn ?? "").replace(/[^0-9]/g, "");
    if (!digits || digits.startsWith("0")) return digits;
    return "0" + digits;
};

/**
 * 화면 표시용 국내 표기로 변환한다 ("1012345678" + KR → "010-1234-5678").
 *
 * 저장 형식은 선행 0 이 없어 사용자가 자기 번호로 인식하기 어렵다.
 * 표시에만 쓰고 전송 값은 그대로 둔다. 국가를 모르거나 파싱에 실패하면 원본을 반환한다.
 */
export const formatCtnForDisplay = (ctn: string, country?: string): string => {
    if (!ctn || !country) return ctn ?? "";
    try {
        const parsed = parsePhoneNumber(ctn, country as Country);
        return parsed ? parsed.formatNational() : ctn;
    } catch {
        return ctn;
    }
};
